import { useInfiniteQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { Activity, DjangoListResponse, TableName } from 'v2/models';
import { PAGE_SIZE } from 'utils/constants';
import { infiniteActivityKeys } from 'v2/queries/activities/query-keys';

export interface IUseGetActivityKeyedProps {
  relatedTable: keyof typeof TableName;
  relatedObjectId?: number;
  filterSelectedOption?: string | boolean;
  filterSearchText?: string;
  system_generated?: boolean;
  ordering?: string;
}

export interface IUseGetActivityProps extends IUseGetActivityKeyedProps {
  pageSize?: number;
  enabled?: boolean;
}

export function getKey(queryParams?: IUseGetActivityKeyedProps) {
  const { relatedTable, relatedObjectId, ...rest } = queryParams || {};
  return [...infiniteActivityKeys.all, relatedTable, relatedObjectId, rest];
}

export function getInvalidationsForNewActivity(
  relatedTable: keyof typeof TableName
) {
  const invalidate = getKey({ relatedTable });
  return [invalidate[0], invalidate[1]];
}

export function useGetActivities({
  relatedTable,
  relatedObjectId,
  filterSelectedOption,
  filterSearchText,
  system_generated,
  ordering = '-timestamp',
  pageSize = PAGE_SIZE,
  enabled = true,
}: IUseGetActivityProps) {
  const infiniteQuery = useInfiniteQuery(
    getKey({
      relatedTable,
      relatedObjectId,
      filterSelectedOption,
      filterSearchText,
      system_generated,
      ordering,
    }),
    async ({ pageParam = 1 }) => {
      const payload = {
        page: pageParam,
        size: pageSize,
        relationships__object_id: relatedObjectId,
        relationships__table_name: relatedTable,
        type: filterSelectedOption,
        system_generated: system_generated,
        ordering: ordering,
        search_text: filterSearchText,
      };
      if (!filterSearchText) {
        delete payload.search_text;
      } else {
        delete payload.type;
      }

      const res = await restFactory.get<DjangoListResponse<Activity>>(
        `/dj/api/v1/activity/`,
        payload
      );

      return {
        ...res,
        prev: pageParam,
        next: pageParam + 1,
      };
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => {
        const hasMorePages = pageSize * lastPage.prev < lastPage.count;
        return hasMorePages ? lastPage.next : undefined;
      },
    }
  );

  return infiniteQuery;
}
